<script setup lang="ts">
import { InfoTip } from '@it-tools/shell'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { OPTICAL_DRIVE_WATTS } from '../model/parts'
import { isValidOtherWatts, LIMITS } from '../model/spec'
import FormRow from './FormRow.vue'

/**
 * Extra watts for anything the form does not list — an optical drive, a sound
 * card, lighting. One number rather than more pickers: the long tail of parts is
 * endless, and each draws little next to the processor and graphics card.
 */
const props = defineProps<{ modelValue: number }>()
const emit = defineEmits<{ 'update:modelValue': [number] }>()

const { t, n } = useI18n()

// Unlike the custom wattage, blank means "nothing extra", so it reads as 0.
// v-model on a number input hands back a number, or the raw string when the
// field does not parse — hence both.
const parseWatts = (value: string | number): number =>
  String(value).trim() === '' ? 0 : Number(value)

// The draft keeps a half-typed or cleared field exactly as typed.
const watts = ref<string | number>(props.modelValue === 0 ? '' : String(props.modelValue))
watch(
  () => props.modelValue,
  (value) => {
    if (!Object.is(parseWatts(watts.value), value)) watts.value = value === 0 ? '' : String(value)
  },
)

const invalid = computed(() => !isValidOtherWatts(parseWatts(watts.value)))
const watt = (value: number): string => t('form.watts', { value: n(value) })
</script>

<template>
  <FormRow :label="t('form.other')" for-id="other-watts">
    <input
      id="other-watts"
      v-model="watts"
      class="other-watts__input mono"
      type="number"
      min="0"
      :max="LIMITS.otherWatts"
      step="1"
      inputmode="numeric"
      placeholder="0"
      autocomplete="off"
      :aria-invalid="invalid"
      :aria-describedby="invalid ? 'other-watts-error' : undefined"
      @input="emit('update:modelValue', parseWatts(watts))"
    />
    <span class="form-row__muted">{{ t('form.wattsUnit') }}</span>
    <InfoTip
      :label="t('form.otherHelpLabel')"
      :text="t('form.otherHelp', { optical: watt(OPTICAL_DRIVE_WATTS) })"
    />
    <template #after>
      <p v-if="invalid" id="other-watts-error" class="form-row__error" role="alert">
        {{ t('form.otherInvalid', { max: n(LIMITS.otherWatts) }) }}
      </p>
    </template>
  </FormRow>
</template>

<style scoped>
.other-watts__input {
  width: 6rem;
}
</style>
